import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../lib/axios.js';
import { debounce } from 'lodash';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table.jsx';
import {
    Card, CardContent, CardHeader, CardTitle,
} from '../../components/ui/card.jsx';
import { Badge } from '../../components/ui/badge.jsx';
import { Button } from '../../components/ui/button.jsx';
import { Input } from '../../components/ui/input.jsx';
import { Skeleton } from '../../components/ui/skeleton.jsx';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select.jsx";
import {
    Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink,
    PaginationNext, PaginationPrevious,
} from "../../components/ui/pagination.jsx";
import { MagnifyingGlassIcon, PlusIcon } from '@radix-ui/react-icons';
import { useToast } from "../../hooks/use-toast.js";
import UserStatus from './UserStatus.jsx';

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        per_page: 10,
        last_page: 0
    });

    const navigate = useNavigate();
    const { toast } = useToast();

    const fetchUsers = useCallback(async (query, page, per_page, role) => {
        setLoading(true);
        try {
            const params = {
                search: query,
                page,
                per_page,
            };
            if (role !== 'all') params.role = role;

            const response = await axios.get('/users', { params });

            setUsers(response.data.users);
            setPagination({
                page: response.data.page,
                total: response.data.total,
                per_page: response.data.per_page,
                last_page: response.data.last_page,
            });
        } catch (error) {
            console.error('Error fetching users:', error);
            toast({
                title: "Error",
                description: "Failed to load users. Please try again.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    const debouncedFetchUsers = useCallback(
        debounce((q, p, l, r) => fetchUsers(q, p, l, r), 300),
        [fetchUsers]
    );

    useEffect(() => {
        fetchUsers(searchTerm, pagination.page, pagination.per_page, roleFilter);
    }, [pagination.page, pagination.per_page, roleFilter]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        setPagination(prev => ({ ...prev, page: 1 }));
        debouncedFetchUsers(val, 1, pagination.per_page, roleFilter);
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const capFirstLetterAndLowerCaseRest = (str) =>
        str ? `${str.charAt(0).toUpperCase()}${str.slice(1).toLowerCase()}` : str;

    const LoadingSkeleton = () => (
        [...Array(pagination.per_page)].map((_, i) => (
            <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-[250px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[200px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
                <TableCell><Skeleton className="h-8 w-[100px]" /></TableCell>
            </TableRow>
        ))
    );

    return (
        <div className="min-h-full min-w-full flex flex-col">
            <Card className="w-full max-w-7xl mx-auto">
                <CardHeader>
                    <CardTitle className="text-3xl font-bold text-center">Users</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-between mb-4">
                        <div className="relative flex-grow max-w-full">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <Input
                                type="text"
                                placeholder="Search by name or email..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="pl-9"
                            />
                        </div>
                        <Button onClick={() => navigate('/users/create')} className="ml-4">
                            <PlusIcon className="w-4 h-4 mr-2" />
                            Create User
                        </Button>
                    </div>

                    <div className='flex justify-between items-center mt-5 mb-4'>
                        <div className="text-sm text-gray-500">
                            Total Users: {pagination.total}
                        </div>
                        <div className="flex items-center space-x-2">
                            <Select value={roleFilter} onValueChange={(value) => {
                                setRoleFilter(value);
                                setPagination(prev => ({ ...prev, page: 1 }));
                                debouncedFetchUsers(searchTerm, 1, pagination.per_page, value);
                            }}>
                                <SelectTrigger className="w-[200px]">
                                    <SelectValue placeholder="Filter by role" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Roles</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="user">User</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[30%]">Name</TableHead>
                                    <TableHead className="w-[30%]">Email</TableHead>
                                    <TableHead className="w-[15%]">Role</TableHead>
                                    <TableHead className="w-[15%]">Status</TableHead>
                                    <TableHead className="w-[10%]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <LoadingSkeleton />
                                ) : users?.length > 0 ? (
                                    users.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell>
                                                <span className='mr-1'>{capFirstLetterAndLowerCaseRest(user.first_name)}</span>
                                                <span>{capFirstLetterAndLowerCaseRest(user.last_name)}</span>
                                            </TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                <Badge variant="secondary">{user.role}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <UserStatus status={user.status} />
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => navigate(`/users/${user.id}`)}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8">
                                            <Badge variant="secondary">No users found</Badge>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {!loading && pagination.last_page > 1 && (
                        <div className="mt-4 flex justify-center">
                            <Pagination currentPage={pagination.page} lastPage={pagination.last_page}>
                                <PaginationContent>
                                    <PaginationItem>
                                        <PaginationPrevious onClick={() => handlePageChange(pagination.page - 1)} />
                                    </PaginationItem>

                                    {[...Array(pagination.last_page)].map((_, index) => {
                                        const pageNumber = index + 1;
                                        if (pageNumber === 1 || pageNumber === pagination.last_page ||
                                            (pageNumber >= pagination.page - 1 && pageNumber <= pagination.page + 1)) {
                                            return (
                                                <PaginationItem key={pageNumber}>
                                                    <PaginationLink onClick={() => handlePageChange(pageNumber)} isActive={pageNumber === pagination.page}>
                                                        {pageNumber}
                                                    </PaginationLink>
                                                </PaginationItem>
                                            );
                                        }
                                        if (pageNumber === 2 || pageNumber === pagination.last_page - 1) {
                                            return <PaginationItem key={pageNumber}><PaginationEllipsis /></PaginationItem>;
                                        }
                                        return null;
                                    })}
                                    <PaginationItem>
                                        <PaginationNext onClick={() => handlePageChange(pagination.page + 1)} />
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default UsersPage;
